import { createColumnHelper } from '@tanstack/react-table';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { LocalizedCurrencyValue, lt, useFeatureFlag } from '@mews/framework';
import { IconName, Link, Stack, Tag } from '@optimus-web/core';
import { GeneralPricingMode, ProductData, ProductPricing, ProductType, ServiceType } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { compactArray } from '@mews/fp';
import { EnterpriseLinkIconButton, SpacedComma } from '@mews-commander/ui';
import { FeatureFlag } from '@mews-commander/enums';
import { useCurrentService } from '../../queries/useCurrentService';
import { LocalizedChargingMode } from '../localization/LocalizedChargingMode';
import { useProductSidePanelContext } from '../sidePanel/ProductSidePanelContext';
import { PricingCell } from './PricingCell';
import { RowActionButton } from './RowActionButton';

const columnHelper = createColumnHelper<ProductData>();
export const useProductTableColumns = () => {
    const { data, id } = useCurrentService();
    const { open } = useProductSidePanelContext();
    const { pricing } = useCurrentEnterprise();
    const allowancesEnabled = useFeatureFlag(FeatureFlag.allowancesEnabled);

    return compactArray([
        columnHelper.accessor('name', {
            id: 'name',
            header: () => lt('Name'),
            cell: (props) => (
                <Link onClick={() => open(props.row.original.id, props.row.original.type)} standalone variant="basic">
                    {props.row.original.name}
                </Link>
            ),
            meta: {
                width: 400,
            },
        }),
        allowancesEnabled
            ? columnHelper.accessor('type', {
                  id: 'type',
                  cell: (props) => {
                      const type = props.getValue();
                      return type === ProductType.Product ? (
                          <Tag variant="denim">{lt('Product')}</Tag>
                      ) : (
                          <Tag variant="lime">{lt('Allowance')}</Tag>
                      );
                  },
                  header: () => lt('Type'),
                  meta: {
                      width: 200,
                  },
              })
            : null,
        columnHelper.accessor('accountingCategoryName', {
            id: 'accountingCategory',
            header: () => lt('AccountingCategory'),
            cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
            meta: {
                width: 200,
            },
        }),
        columnHelper.accessor('pricing', {
            id: 'price',
            header: () => (pricing === GeneralPricingMode.Gross ? lt('BasePrice') : lt('BasePriceExcludeTax')),
            cell: (props) => <PricingCell pricing={props.getValue()} />,
        }),
        allowancesEnabled
            ? columnHelper.accessor('allowance', {
                  id: 'allowance',
                  header: () => 'Allowance',
                  cell: (props) => {
                      const allowance = props.getValue();
                      return allowance ? (
                          <LocalizedCurrencyValue
                              value={{
                                  value: allowance.creditAmount,
                                  currency: allowance.currency,
                              }}
                          />
                      ) : (
                          UnicodeEntity.Dash
                      );
                  },
                  meta: {
                      width: 200,
                  },
              })
            : null,
        columnHelper.accessor('pricing', {
            id: 'tax',
            header: () => lt('Tax'),
            cell: (props) => {
                const {
                    value: { taxRates },
                } = props.getValue();
                return <>{taxRates.length > 0 ? taxRates.join(SpacedComma) : UnicodeEntity.Dash}</>;
            },
            meta: {
                width: 200,
            },
        }),
        data.discriminator === ServiceType.Bookable
            ? columnHelper.accessor('charging', {
                  id: 'charging',
                  header: () => lt('Charging'),
                  cell: (props) => (
                      <LocalizedChargingMode bookableServiceData={data.value} charging={props.getValue()} />
                  ),
                  meta: {
                      width: 200,
                  },
              })
            : null,
        columnHelper.accessor('ordering', {
            id: 'ordering',
            header: () => lt('Ordering'),
            cell: (props) => props.getValue(),
            meta: {
                width: 200,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            meta: { align: 'end', width: 200 },
            cell: ({ row }) => (
                <Stack>
                    {data.discriminator === ServiceType.Bookable &&
                        row.original.pricing.discriminator === ProductPricing.Absolute && (
                            <EnterpriseLinkIconButton
                                icon={IconName.Pricing}
                                type="button"
                                tooltip={lt('Pricing')}
                                size="small"
                                variant="ghost"
                                to="Product/Pricing/$id"
                                params={{
                                    id: row.original.id,
                                }}
                                search={{
                                    OwnerId: id,
                                }}
                            />
                        )}
                    <RowActionButton productId={row.original.id} productType={row.original.type} />
                </Stack>
            ),
        }),
    ]);
};
